import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Truck, LogIn, AlertCircle, CheckCircle2,
  MapPin, Package, ShieldCheck, ChevronLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import LoadingNoteSessionHub from '../components/operations/LoadingNoteSessionHub';
import NoteTypeSelector from '../components/operations/NoteTypeSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const extractErr = (e, fb = 'Échec') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => (typeof x === 'string' ? x : (x?.msg || JSON.stringify(x)))).join(' · ');
  return JSON.stringify(d);
};

/**
 * Page publique de signature des Bons de chargement / déchargement.
 * Accès via /sign/:opId
 *
 * Flow :
 *   1. Écran d'auth légère : code 4 chiffres + nom opérateur → exchange-code
 *   2. Écran de travail : liste des 2 bons (loading + unloading) avec édition + signatures
 *
 * Optimisé tablette + mobile (PM Spec Q6=c)
 */
const PublicSignPage = () => {
  const { opId } = useParams();
  const [search] = useSearchParams();

  // Persist token sur cette opération
  const tokenKey = `baobab_sign_token_${opId}`;
  const [token, setToken] = useState(() => sessionStorage.getItem(tokenKey) || '');
  const [operatorName, setOperatorName] = useState(() => sessionStorage.getItem(`${tokenKey}_op`) || '');
  const [operation, setOperation] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // Form login
  const [code, setCode] = useState(search.get('code') || '');
  const [name, setName] = useState('');
  const [authenticating, setAuthenticating] = useState(false);

  // PM Spec UI 05/05/2026 : sélection du bon à traiter (null = page de choix)
  // Auto-select depuis URL `?type=loading|unloading` quand on arrive via lien ciblé
  const [selectedNoteType, setSelectedNoteType] = useState(() => {
    const t = search.get('type');
    return (t === 'loading' || t === 'unloading') ? t : null;
  });
  const [downloadingType, setDownloadingType] = useState(null);

  // Bug fix 16/02/2026 — Écran terminal après signature (évite démontage prématuré du flow)
  // Quand un flow appelle onCompleted, on bascule sur cet écran de remerciement
  // (avec download PDF + bouton Terminer) au lieu de re-monter le wizard depuis l'étape 1.
  const [signedSuccess, setSignedSuccess] = useState(null); // null | { type, party, noteNumber, noteId }
  const [downloadingFinal, setDownloadingFinal] = useState(false);

  // PM Bug fix 17/02/2026 — noteId scopé renvoyé par /exchange-link-token (multi-rotation)
  // Permet aux flows de cibler la BONNE note (signatureLinks est strippé du list public).
  const [scopedNoteId, setScopedNoteId] = useState(
    () => sessionStorage.getItem(`${tokenKey}_noteId`) || null
  );

  // Bug fix 16/02/2026 — Mémoise authHeaders pour éviter les boucles de re-render dans les Flows
  // Bug fix 20/05/2026 — utilise le VRAI JWT scopé (avant: 'Bearer cookie-auth' faux sentinel
  // contaminé par la migration cookie-auth, ce qui faisait échouer toutes les signatures
  // depuis la tablette externe avec 401 → "Session expirée").
  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  // PM Spec 07/05/2026 sprint 2 — Lien ciblé par party (no-agent flow)
  const partyScope = search.get('party') || null;       // 'shipper'|'consignee'|'driver'|'observer'
  const linkId = search.get('linkId') || null;
  const linkToken = search.get('t') || null;            // Token opaque (auth primaire)

  // Auto-exchange : si l'URL contient ?linkId=...&token=..., échange contre JWT scopé.
  // CRITIQUE : on ne ré-exchange JAMAIS un token déjà utilisé (single-use côté backend).
  // On sentinelle dans sessionStorage pour empêcher la boucle infinie qui causait des 429.
  const exchangeAttemptKey = `${tokenKey}_exchanged_link`;
  useEffect(() => {
    let cancelled = false;
    const autoExchange = async () => {
      if (!linkToken || !linkId || token) return;
      // Anti-loop : si on a déjà tenté avec ce linkToken, ne pas retry
      if (sessionStorage.getItem(exchangeAttemptKey) === linkToken) {
        return;
      }
      sessionStorage.setItem(exchangeAttemptKey, linkToken);
      setAuthenticating(true);
      try {
        const r = await axios.post(`${API}/public/sign/exchange-link-token`, {
          operationId: opId,
          linkId,
          token: linkToken,
          // operatorName facultatif — le backend utilisera link.targetName si vide
        });
        if (cancelled) return;
        const t = r.data?.token;
        const opName = r.data?.operatorName || '';
        const nId = r.data?.noteId || null;
        if (!t) throw new Error('Token manquant');
        sessionStorage.setItem(tokenKey, t);
        sessionStorage.setItem(`${tokenKey}_op`, opName);
        if (nId) sessionStorage.setItem(`${tokenKey}_noteId`, nId);
        setToken(t);
        setOperatorName(opName);
        setScopedNoteId(nId);
      } catch (err) {
        if (cancelled) return;
        const httpCode = err?.response?.status;
        if (httpCode === 429) {
          setAuthError('Trop de tentatives. Patientez 60 secondes puis rescannez le QR code.');
        } else {
          setAuthError(extractErr(err, 'Lien invalide ou expiré'));
        }
      } finally {
        if (!cancelled) setAuthenticating(false);
      }
    };
    autoExchange();
    return () => { cancelled = true; };
  }, [linkToken, linkId, opId, tokenKey, token, exchangeAttemptKey]);

  const axiosAuth = useCallback(() => {
    if (!token) return null;
    return axios.create({
      baseURL: API,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  const loadContext = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const cli = axiosAuth();
      const [ctx, list] = await Promise.all([
        cli.get(`/public/sign/${opId}/context`),
        cli.get(`/public/sign/${opId}/loading-notes`),
      ]);
      setOperation(ctx.data?.operation || null);
      setOperatorName(ctx.data?.operatorName || operatorName);
      if (ctx.data?.noteId) {
        setScopedNoteId(ctx.data.noteId);
        sessionStorage.setItem(`${tokenKey}_noteId`, ctx.data.noteId);
      }
      setNotes(Array.isArray(list.data) ? list.data : []);
      setAuthError('');
    } catch (err) {
      const httpCode = err?.response?.status;
      if (httpCode === 401) {
        sessionStorage.removeItem(tokenKey);
        sessionStorage.removeItem(`${tokenKey}_op`);
        // Permet de re-échanger le linkToken au prochain mount (nouveau scan)
        sessionStorage.removeItem(exchangeAttemptKey);
        setToken('');
        setAuthError('Session expirée, reconnectez-vous avec un nouveau code ou rescannez le QR.');
      } else if (httpCode === 429) {
        setAuthError('Trop de requêtes. Patientez 60 secondes avant de réessayer.');
        toast({ title: 'Limite atteinte', description: 'Patientez 60s puis rafraîchissez la page.', variant: 'destructive' });
      } else {
        toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
      }
    } finally { setLoading(false); }
  }, [token, opId, axiosAuth, tokenKey, operatorName, exchangeAttemptKey]);

  useEffect(() => { if (token) loadContext(); }, [token, loadContext]);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setAuthError('');
    if (!/^\d{3,6}$/.test(code.trim())) {
      setAuthError('Le code doit être numérique (4 chiffres)');
      return;
    }
    if (!name.trim() || name.trim().length < 2) {
      setAuthError('Saisissez votre nom (min. 2 caractères)');
      return;
    }
    setAuthenticating(true);
    try {
      const r = await axios.post(`${API}/public/sign/exchange-code`, {
        operationId: opId,
        code: code.trim(),
        operatorName: name.trim(),
      });
      const t = r.data?.token;
      if (!t) throw new Error('Token manquant');
      sessionStorage.setItem(tokenKey, t);
      sessionStorage.setItem(`${tokenKey}_op`, name.trim());
      setToken(t);
      setOperatorName(name.trim());
    } catch (err) {
      setAuthError(extractErr(err, 'Code refusé'));
    } finally { setAuthenticating(false); }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(tokenKey);
    sessionStorage.removeItem(`${tokenKey}_op`);
    sessionStorage.removeItem(`${tokenKey}_noteId`);
    setToken('');
    setNotes([]);
    setOperation(null);
    setScopedNoteId(null);
  };

  // ─────────────────────────────────────────
  // Écran 1 : authentification légère
  // ─────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-emerald-100 shadow-lg" data-testid="sign-login-card">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center mb-2">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-xl">Signature Bons de transport</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Saisissez le code reçu par email et votre nom pour accéder à l'opération.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="sign-code" className="text-sm font-medium">Code de signature (4 chiffres)</Label>
                <Input
                  id="sign-code"
                  data-testid="sign-code-input"
                  type="text" inputMode="numeric" pattern="[0-9]{4}" maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  className="h-12 text-center text-2xl tracking-[0.4em] font-mono"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="sign-name" className="text-sm font-medium">Votre nom / matricule</Label>
                <Input
                  id="sign-name"
                  data-testid="sign-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex. KOUAME Jean - Opérateur site"
                  className="h-11"
                />
              </div>
              {authError && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 text-red-700 text-sm"
                     data-testid="sign-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {authError}
                </div>
              )}
              <Button
                type="submit"
                disabled={authenticating}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700"
                data-testid="sign-login-submit"
              >
                {authenticating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                Accéder à l'opération
              </Button>
              <p className="text-[11px] text-slate-400 text-center pt-2">
                <ShieldCheck className="inline w-3 h-3 mr-1" />
                Accès sécurisé par code temporaire. Vos actions sont tracées (audit).
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─────────────────────────────────────────
  // Écran 2 : workspace wizard
  // ─────────────────────────────────────────
  const loadingNote = notes.find(n => n.type === 'loading') || null;
  const unloadingNote = notes.find(n => n.type === 'unloading') || null;
  const opRefDisplay = operation?.reference || operation?.operationNumber || operation?.requestNumber || opId.slice(0, 8) + '…';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-800">Signature bons de transport</div>
              <div className="text-xs text-slate-500">
                Connecté : <span className="font-medium">{operatorName}</span>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="sign-logout-btn">
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Opération context (référence métier en évidence) */}
        {operation && (
          <Card data-testid="sign-op-context" className="border-emerald-100">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-600 text-white border-emerald-600 font-mono text-sm px-3 py-1">
                    {opRefDisplay}
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                    Statut : {operation.status || '—'}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm pt-2 border-t border-slate-100">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                  <div>
                    <div className="text-slate-400 text-xs">Trajet</div>
                    <div className="font-medium">{operation.departure || '—'}</div>
                    <div className="text-slate-500">→ {operation.arrival || '—'}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Package className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                  <div>
                    <div className="text-slate-400 text-xs">Marchandise & véhicule</div>
                    <div className="font-medium">{operation.merchandiseType || '—'}{operation.weight ? ` — ${operation.weight}T` : ''}</div>
                    <div className="text-slate-500 text-xs">
                      {operation.vehicleType || '—'}{operation.licensePlate ? ` (${operation.licensePlate})` : ''}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && (
          <div className="text-center py-8 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…
          </div>
        )}

        {/* Page de choix du bon (PM Spec UI 05/05/2026) — affichée par défaut après login */}
        {!loading && operation && !selectedNoteType && !signedSuccess && (
          <NoteTypeSelector
            notes={notes}
            operation={operation}
            onSelect={(type) => setSelectedNoteType(type)}
            onDownload={async (type, note) => {
              if (!note?.id) return;
              setDownloadingType(type);
              try {
                const r = await fetch(`${API}/public/sign/${opId}/loading-notes/${note.id}/pdf`, {
                });
                if (!r.ok) throw new Error('PDF indisponible');
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `${note.noteNumber}.pdf`; a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                toast({ title: 'Erreur', description: err?.message || 'Téléchargement impossible', variant: 'destructive' });
              } finally { setDownloadingType(null); }
            }}
            downloadingType={downloadingType}
          />
        )}

        {/* Écran terminal "Merci, c'est terminé" (PM Bug fix 16/02/2026)
            Affiché APRÈS qu'un flow a appelé onCompleted, pour éviter le démontage
            du wizard qui faisait revenir à l'étape 1. */}
        {signedSuccess && (
          <Card className="border-emerald-200 bg-emerald-50 max-w-2xl mx-auto" data-testid="sign-final-success">
            <CardContent className="p-8 space-y-6 text-center">
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-800">Merci, c'est terminé !</div>
                <div className="text-sm text-slate-600 mt-2">
                  Votre signature pour le bon de{' '}
                  <b>{signedSuccess.type === 'loading' ? 'chargement' : 'déchargement'}</b>{' '}
                  a bien été enregistrée.
                </div>
                {signedSuccess.noteNumber && (
                  <div className="text-xs font-mono text-slate-500 mt-2">
                    Référence : {signedSuccess.noteNumber}
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                {signedSuccess.noteId && (
                  <Button
                    variant="outline"
                    disabled={downloadingFinal}
                    onClick={async () => {
                      setDownloadingFinal(true);
                      try {
                        const r = await fetch(`${API}/public/sign/${opId}/loading-notes/${signedSuccess.noteId}/pdf`, {
                        });
                        if (!r.ok) throw new Error('PDF indisponible');
                        const blob = await r.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `${signedSuccess.noteNumber || 'bon'}.pdf`; a.click();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        toast({ title: 'Erreur', description: err?.message || 'Téléchargement impossible', variant: 'destructive' });
                      } finally { setDownloadingFinal(false); }
                    }}
                    data-testid="sign-success-download-pdf"
                  >
                    {downloadingFinal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Télécharger le PDF
                  </Button>
                )}
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    handleLogout();
                    setSignedSuccess(null);
                    setSelectedNoteType(null);
                  }}
                  data-testid="sign-success-close"
                >
                  Terminer
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 pt-2">
                Vous pouvez fermer cette page. Les autres signataires seront notifiés automatiquement.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Wizard du bon sélectionné — bouton retour vers la page de choix */}
        {!loading && operation && selectedNoteType && !signedSuccess && (
          <div className="space-y-3">
            <Button variant="outline" size="sm"
                    onClick={() => { setSelectedNoteType(null); loadContext(); }}
                    className="text-slate-600 hover:text-emerald-700"
                    data-testid="back-to-selector-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour au choix du bon
            </Button>
            <LoadingNoteSessionHub
              mode="distributed"
              noteType={selectedNoteType}
              operation={operation}
              operatorName={operatorName}
              endpointPrefix={`${API}/public/sign/${opId}/loading-notes`}
              authHeaders={authHeaders}
              partyScope={partyScope}
              linkId={linkId}
              noteId={scopedNoteId}
              sessionRole="distributed"
              onCompleted={() => {
                // Bug fix 16/02/2026 — bascule sur écran terminal AVANT le démontage
                // PM Bug fix 17/02/2026 — cible la note scopée (multi-rotation)
                let targetNote = null;
                if (scopedNoteId) {
                  targetNote = notes.find(n => n.id === scopedNoteId);
                }
                if (!targetNote) {
                  targetNote = notes.find(n => n.type === selectedNoteType);
                }
                setSignedSuccess({
                  type: selectedNoteType,
                  party: partyScope,
                  noteNumber: targetNote?.noteNumber || null,
                  noteId: targetNote?.id || scopedNoteId || null,
                });
              }}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicSignPage;
